import React from 'react';
import PropTypes from 'prop-types';
import { LuMessageCircle, LuUserPlus } from 'react-icons/lu';

import { DashedPill, Pill } from '../../../../styles/dunning/base';
import {
   Avatar,
   CaseAmount,
   CaseAmountRow,
   CaseCard,
   CaseCount,
   CaseDoc,
   CaseIdentity,
   CaseName,
   CasePillsRow,
   CaseTopRow,
} from '../../../../styles/dunning/kanban';
import {
   agingInfo,
   canMoveCase,
   caseFigures,
   commitPill,
   needsAssignee,
   treatChip,
} from '../utils/derive';
import { formatBRL, formatDoc, getInitials } from '../../utils/dunningFormat';

// ---------------------------------------------------------------------------
// Card do caso no kanban. Só apresenta — bloqueios de drag/drop são do board.
//
// Arrastável = caso OPEN da trilha INTERNA (`canMoveCase`). Caso do escritório
// não muda de raia por design (0103 §3.2): o backend responde 422
// `external_case_not_movable`, e o único jeito de tirar título de lá é
// "Devolver à régua".
//
// Os números vêm de `caseFigures` (0102): caso recebido mostra o RECEBIDO, não
// o "em aberto" que é zero por definição. O atraso só existe no caso aberto —
// fora dele a pill sumiu em vez de virar "sem título aberto" ocupando espaço.
// ---------------------------------------------------------------------------

export default function KanbanCard({
   caseRow,
   today,
   isDragging,
   onOpen,
   onDragStart,
   onDragEnd,
}) {
   const figures = caseFigures(caseRow);
   const aging = agingInfo(caseRow.oldest_due_date, today);
   const commit = commitPill(caseRow, today);
   const treat = treatChip(caseRow.interactions_count);
   const draggable = canMoveCase(caseRow);
   const awaitingAssignee = needsAssignee(caseRow);
   const assigneeName = caseRow.assigned_user?.nome ?? 'Sem responsável';

   const TreatComponent = treat.tier === 'zero' ? DashedPill : Pill;

   return (
      <CaseCard
         draggable={draggable}
         $dragging={isDragging}
         onClick={onOpen}
         onDragStart={(event) => {
            if (!draggable) return;
            // Payload mínimo pro DnD nativo funcionar cross-browser; o estado
            // de verdade (id arrastado) fica no container.
            const { dataTransfer } = event;
            dataTransfer.effectAllowed = 'move';
            dataTransfer.setData('text/plain', String(caseRow.id));
            onDragStart(caseRow.id);
         }}
         onDragEnd={onDragEnd}
      >
         <CaseTopRow>
            <CaseIdentity>
               <CaseName>{caseRow.customer_name}</CaseName>
               <CaseDoc>{formatDoc(caseRow.customer_doc)}</CaseDoc>
            </CaseIdentity>
            <Avatar $empty={!caseRow.assigned_user} title={assigneeName}>
               {getInitials(caseRow.assigned_user?.nome)}
            </Avatar>
         </CaseTopRow>

         <CaseAmountRow>
            <CaseAmount>{formatBRL(figures.amount)}</CaseAmount>
            <CaseCount>{figures.countLabel}</CaseCount>
         </CaseAmountRow>

         <CasePillsRow>
            {/* Armadilha do 0103 §9: o caso externo nasce SEM responsável, e o
                operador do escritório (sem read_all) só vê o que está atribuído
                a ele — nesse intervalo o caso existe e ninguém do escritório o
                enxerga. Sem este aviso, a operação lê como "o título sumiu". */}
            {awaitingAssignee && (
               <Pill
                  $variant="alert"
                  title="O escritório só enxerga casos atribuídos a ele — defina o responsável"
               >
                  <LuUserPlus size={11} />
                  aguardando atribuição
               </Pill>
            )}
            {/* Caso aberto sem título aberto é anomalia — segue dita em voz
                alta. Encerrado não tem atraso: a pill some. */}
            {figures.showAging && (
               <Pill $variant={aging.variant}>
                  {aging.days === null
                     ? 'sem título aberto'
                     : `atraso ${aging.days} dias`}
               </Pill>
            )}
            <Pill $variant={commit.variant}>{commit.label}</Pill>
            {/* Só o número — balão + contagem é linguagem de rede social,
                dispensa o texto "tratativa(s)" (decisão 2026-08-14). */}
            <TreatComponent
               $variant={treat.tier === 'many' ? 'info' : 'neutral'}
               title="Tratativas registradas no caso"
            >
               <LuMessageCircle size={11} />
               {caseRow.interactions_count ?? 0}
            </TreatComponent>
            {caseRow.reopened_count > 0 && (
               <Pill
                  $variant="reopened"
                  title="Caso reaberto após renegociação"
               >
                  reaberto {caseRow.reopened_count}x
               </Pill>
            )}
         </CasePillsRow>
      </CaseCard>
   );
}

KanbanCard.propTypes = {
   // Shape do backend (snake_case) — validado no contrato, não aqui.
   // eslint-disable-next-line react/forbid-prop-types
   caseRow: PropTypes.object.isRequired,
   today: PropTypes.string.isRequired,
   isDragging: PropTypes.bool.isRequired,
   onOpen: PropTypes.func.isRequired,
   onDragStart: PropTypes.func.isRequired,
   onDragEnd: PropTypes.func.isRequired,
};
